import Phaser from 'phaser';
import type { Circuit } from '@multirx/shared/types/track.js';
import type { RaceHistoryReplay } from '@multirx/shared/types/raceHistory.js';
import { rankingAtRound, resolveBoardBeforeRound } from '@multirx/shared/game/archiveReplay.js';
import { ArchiveReplayScene } from '../game/ArchiveReplayScene.js';
import {
  attachPhaserCanvasToArchiveMount,
  getPhaserGame,
  resizeArchivePhaserCanvas,
} from '../game/phaserApp.js';
import { fetchCarById } from '../cars/api.js';
import {
  mountCircuit3dPreview,
  type Circuit3dPreviewHandle,
  type LivePlayer3D,
} from '../circuits/circuit3dPreview.js';
import { createPillButton } from '../ui/pillButton.js';
import { getOwnDriverId } from '../auth/ownDriverLabel.js';
import { buildArchiveRoundHudBadges } from './archiveReplayHud.js';

export type ArchiveReplayController = {
  showTurn: (turnNumber: number) => void;
  resetView: () => void;
  setRoundDurationMs: (ms: number) => void;
  /** Centre la caméra 3D sur un pilote (clic roster). */
  followPlayer: (playerIndex: number) => void;
  getFollowedPlayerIndex: () => number | null;
  onTurnChange: (cb: (turn: number) => void) => void;
  onTurnSettled: (cb: (turn: number) => void) => void;
  destroy: () => void;
};

function getArchiveScene(game: Phaser.Game): ArchiveReplayScene | undefined {
  return game.scene.getScene('ArchiveReplayScene') as ArchiveReplayScene | undefined;
}

/** Résout le modèle 3D de chaque participant (carId → model3dPath).
 * Sans assignation manuelle, `resolveCar3dModelUrl` utilise la berline générique. */
async function buildLivePlayers3D(
  replay: RaceHistoryReplay,
  numParticipants: number
): Promise<LivePlayer3D[]> {
  // Grille de départ (avant le tour 1) — jamais afterRound(0) qui clamp en tour 1.
  const board = resolveBoardBeforeRound(replay, 1, numParticipants);
  return Promise.all(
    replay.participants.map(async (p): Promise<LivePlayer3D> => {
      let model3dPath: string | null = null;
      if (p.carId != null) {
        try {
          model3dPath = (await fetchCarById(p.carId)).model3dPath ?? null;
        } catch {
          /* Voiture supprimée depuis : repli sur le matching par nom. */
        }
      }
      const snap = board.find((s) => s.playerIndex === p.playerIndex);
      return {
        id: p.playerIndex,
        carName: p.carName,
        model3dPath,
        position: {
          mainPathIndex: snap?.pathIndex ?? 0,
          jokerBranchIndex: snap?.jokerBranchIndex ?? null,
          startLaneIndex: snap?.startLaneIndex ?? null,
        },
      };
    })
  );
}

function leaderPlayerIndex(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): number | null {
  const ranking = rankingAtRound(replay, round, numParticipants);
  return ranking[0]?.playerIndex ?? replay.participants[0]?.playerIndex ?? null;
}

export function mountArchiveReplayBoard(
  host: HTMLElement,
  circuit: Circuit,
  replay: RaceHistoryReplay,
  numParticipants: number,
  initialTurn = 1,
  circuitName?: string | null
): ArchiveReplayController {
  host.innerHTML = '';
  host.classList.add('archives-replay-board-host--ready');

  const mount = document.createElement('div');
  mount.className = 'archives-replay-phaser-mount';
  const mountHeight = Math.max(280, Math.round((host.clientWidth || 640) * 0.52));
  mount.style.width = '100%';
  mount.style.height = `${mountHeight}px`;
  mount.style.flex = '1 1 auto';
  mount.style.minHeight = '280px';

  const view3d = document.createElement('div');
  view3d.className = 'archives-replay-3d-mount';
  view3d.style.width = '100%';
  view3d.style.height = `${mountHeight}px`;
  view3d.style.flex = '1 1 auto';
  view3d.style.minHeight = '280px';

  const resetBtn = createPillButton('Centrer', {
    variant: 'soft',
    small: true,
    class: 'archives-replay-reset-btn',
  });
  if (resetBtn instanceof HTMLButtonElement) {
    resetBtn.title = 'Recentrer sur le circuit';
    resetBtn.setAttribute('aria-label', 'Recentrer sur le circuit');
  }

  const toggleViewBtn = createPillButton('Voir en 2D', {
    variant: 'soft',
    small: true,
    class: 'archives-replay-view-toggle-btn',
  });

  const controls = document.createElement('div');
  controls.className = 'archives-replay-controls';
  controls.append(toggleViewBtn, resetBtn);

  host.append(mount, view3d, controls);

  let viewMode: '2d' | '3d' = '3d';
  let preview3d: Circuit3dPreviewHandle | null = null;
  let currentRound = initialTurn;
  let followedPlayerId = leaderPlayerIndex(replay, initialTurn, numParticipants);
  let userPickedFollow = false;

  const refreshHudBadges = (round: number): void => {
    currentRound = round;
    if (!userPickedFollow) {
      followedPlayerId = leaderPlayerIndex(replay, round, numParticipants);
    }
    getArchiveScene(getPhaserGame())?.setPlayerHudLabels(round);
    if (viewMode !== '3d' || !preview3d?.setCarHudBadge) {
      preview3d?.clearCarHudBadges?.();
      return;
    }
    const badges = buildArchiveRoundHudBadges(replay, round, numParticipants, getOwnDriverId());
    for (const [playerIndex, html] of badges) {
      preview3d.setCarHudBadge(playerIndex, html);
    }
  };

  const applyViewMode = (): void => {
    mount.hidden = viewMode === '3d';
    view3d.hidden = viewMode !== '3d';
    resetBtn.hidden = viewMode === '3d';
    if (toggleViewBtn instanceof HTMLButtonElement) {
      toggleViewBtn.textContent = viewMode === '3d' ? 'Voir en 2D' : 'Voir en 3D';
    }
    refreshHudBadges(currentRound);
  };
  applyViewMode();

  const wire3DSync = (): void => {
    getArchiveScene(getPhaserGame())?.set3DSyncHandler(
      (playerIndex, trackPos, durationMs, options) => {
        preview3d?.movePlayerTo?.(playerIndex, trackPos, durationMs, options);
      }
    );
  };

  void buildLivePlayers3D(replay, numParticipants).then((players) => {
    preview3d?.dispose();
    preview3d = mountCircuit3dPreview(view3d, circuit, players, {
      circuitName,
      initialCameraMode: 'active',
      getActivePlayerId: () => followedPlayerId,
    });
    preview3d.setCameraMode?.('active');
    wire3DSync();
    refreshHudBadges(currentRound);
  });

  toggleViewBtn.addEventListener('click', () => {
    viewMode = viewMode === '3d' ? '2d' : '3d';
    applyViewMode();
  });

  const game = getPhaserGame();

  if (game.scene.isActive('GameScene')) {
    game.scene.pause('GameScene');
  }
  if (game.scene.isActive('ArchiveReplayScene')) {
    game.scene.stop('ArchiveReplayScene');
  }

  let resizeTimer: ReturnType<typeof setTimeout> | null = null;
  let lastMountSize = '';
  const layoutArchiveCanvas = (): void => {
    const { w, h } = resizeArchivePhaserCanvas(mount);
    const key = `${w}x${h}`;
    if (key === lastMountSize) return;
    lastMountSize = key;
    getArchiveScene(game)?.events.emit('archive-replay-resize');
  };
  const scheduleLayout = (): void => {
    if (resizeTimer != null) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resizeTimer = null;
      layoutArchiveCanvas();
    }, 200);
  };

  attachPhaserCanvasToArchiveMount(mount);

  const turnListeners: Array<(turn: number) => void> = [];
  const settledListeners: Array<(turn: number) => void> = [];
  const wireSceneEvents = (s: ArchiveReplayScene | undefined): void => {
    if (!s) return;
    s.events.off('archive-turn');
    s.events.off('archive-turn-settled');
    s.events.on('archive-turn', (t: number) => {
      refreshHudBadges(t);
      for (const cb of turnListeners) cb(t);
    });
    s.events.on('archive-turn-settled', (t: number) => {
      refreshHudBadges(t);
      for (const cb of settledListeners) cb(t);
    });
  };

  game.scene.start('ArchiveReplayScene', {
    circuit,
    replay,
    numParticipants,
    initialTurn,
  });
  wireSceneEvents(getArchiveScene(game));
  wire3DSync();

  requestAnimationFrame(() => layoutArchiveCanvas());

  const ro =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => scheduleLayout()) : null;
  ro?.observe(mount);

  const bindScene = (): ArchiveReplayScene | undefined => getArchiveScene(game);

  resetBtn.addEventListener('click', () => bindScene()?.resetCamera());

  const startArchiveScene = (turnNumber: number): void => {
    if (game.scene.isActive('ArchiveReplayScene')) {
      game.scene.stop('ArchiveReplayScene');
    }
    attachPhaserCanvasToArchiveMount(mount);
    game.scene.start('ArchiveReplayScene', {
      circuit,
      replay,
      numParticipants,
      initialTurn: turnNumber,
    });
    wireSceneEvents(getArchiveScene(game));
    wire3DSync();
  };

  return {
    showTurn(turnNumber: number): void {
      const s = bindScene();
      if (s?.scene.isActive()) {
        s.showTurn(turnNumber);
        return;
      }
      startArchiveScene(turnNumber);
    },
    resetView(): void {
      bindScene()?.resetCamera();
    },
    setRoundDurationMs(ms: number): void {
      bindScene()?.setRoundDurationMs(ms);
    },
    followPlayer(playerIndex: number): void {
      userPickedFollow = true;
      followedPlayerId = playerIndex;
      preview3d?.setCameraMode?.('active');
    },
    getFollowedPlayerIndex(): number | null {
      return followedPlayerId;
    },
    onTurnChange(cb: (turn: number) => void): void {
      turnListeners.push(cb);
    },
    onTurnSettled(cb: (turn: number) => void): void {
      settledListeners.push(cb);
    },
    destroy(): void {
      ro?.disconnect();
      preview3d?.clearCarHudBadges?.();
      preview3d?.dispose();
      preview3d = null;
      if (game.scene.isActive('ArchiveReplayScene')) {
        getArchiveScene(game)?.set3DSyncHandler(null);
        game.scene.stop('ArchiveReplayScene');
      }
      if (host.isConnected) {
        host.replaceChildren();
      }
      host.classList.remove('archives-replay-board-host--ready');
    },
  };
}
